import { palette } from '../components/styles/colors'
import { isLevelPremiumOrHigher } from './userProperties/level'

export enum ProcessorName {
    CCBILL = 'CCBill',
    EPOCH = 'Epoch',
    NETBILLING = 'Netbilling',
    ROCKETGATE = 'RocketGate',
    SEGPAY = 'Segpay',
}

export enum ProcessorID {
    CCBILL = 4,
    NETBILLING = 9,
    ROCKETGATE = 23,
    SEGPAY = 24,
}

export const processorNameToID = (name: ProcessorName) =>
    ({
        [ProcessorName.CCBILL]: ProcessorID.CCBILL,
        [ProcessorName.NETBILLING]: ProcessorID.NETBILLING,
        [ProcessorName.ROCKETGATE]: ProcessorID.ROCKETGATE,
        [ProcessorName.SEGPAY]: ProcessorID.SEGPAY,
        [ProcessorName.EPOCH]: ProcessorID.SEGPAY,
    })[name]

export enum PackageID {
    TOKENS_200 = 600,
    TOKENS_550 = 601,
    TOKENS_900 = 602,
    TOKENS_1875 = 603,
    TOKENS_3775 = 604,
    TOKENS_7575 = 605,
}

export enum PackageProduct {
    TOKENS_10 = '635+Credit+Card',
    TOKENS_15 = '636+Credit+Card',
    TOKENS_20 = '637+Credit+Card',
    TOKENS_25 = '620+Credit+Card',
    TOKENS_30 = '621+Credit+Card',
    TOKENS_35 = '622+Credit+Card',
    TOKENS_40 = '623+Credit+Card',
    TOKENS_45 = '624+Credit+Card',
    TOKENS_50 = '625+Credit+Card',
    TOKENS_55 = '626+Credit+Card',
    TOKENS_60 = '627+Credit+Card',
    TOKENS_65 = '628+Credit+Card',
    TOKENS_70 = '629+Credit+Card',
    TOKENS_75 = '630+Credit+Card',
    TOKENS_80 = '631+Credit+Card',
    TOKENS_85 = '632+Credit+Card',
    TOKENS_90 = '633+Credit+Card',
    TOKENS_95 = '634+Credit+Card',
    TOKENS_200 = '600+Credit+Card',
    TOKENS_550 = '601+Credit+Card',
    TOKENS_900 = '602+Credit+Card',
    TOKENS_1875 = '603+Credit+Card',
    TOKENS_3775 = '604+Credit+Card',
    TOKENS_7575 = '605+Credit+Card',
}

export enum PackagePrice {
    TOKENS_200 = 19.99,
    TOKENS_550 = 49.99,
    TOKENS_900 = 74.99,
    TOKENS_1875 = 149.99,
    TOKENS_3775 = 299.99,
    TOKENS_7575 = 599.99,
}

export enum PackageVolume {
    TOKENS_200 = 200,
    TOKENS_550 = 550,
    TOKENS_900 = 900,
    TOKENS_1875 = 1875,
    TOKENS_3775 = 3775,
    TOKENS_7575 = 7575,
}

export interface Product {
    amount: PackagePrice
    available_processors: Array<ProcessorID>
    available: boolean
    description: string
    id: PackageID
    name: string
    volume_type: 'token'
    volume: PackageVolume
}

export type Products = Record<PackageID, Product>

export interface PreviousPurchase {
    amount: PackagePrice
    days_since: number
    processor_id: ProcessorID
    product_id: PackageID
    transaction_date: string
}

export interface ProcessorNameID {
    id: ProcessorID
    name: ProcessorName
}

export interface ProcessorDetails {
    description: string
    display_name: string
    id: ProcessorID | number
    name: ProcessorName | string
    payment_types: string
}

export interface PurchaseLimits {
    brief?: unknown
    code?: unknown
    daily_token_limit_amount: number
    description?: string
    err?: unknown
    total_limit: number
}

export interface PurchaseInfoResponse {
    code?: number
    description?: number
    err?: number
    available_quickbuy_processors: Array<ProcessorID | number>
    disable_in_chat_quickbuy: 0 | 1
    previous_purchase?: PreviousPurchase
    processor_priority: Array<ProcessorID>
    processors: Record<ProcessorID, ProcessorDetails>
    products: Record<PackageID, Product>
    purchase_limits: PurchaseLimits
    success: boolean
}

export interface TokenProduct {
    amount: PackagePrice | number
    description: string
    id: PackageID | number
    name: string
    processors: Array<ProcessorID | number>
    processorsMap?: Record<ProcessorID, ProcessorDetails>
    volume_type: 'token'
    volume: PackageVolume | number
}

export interface ITokenPurchaseState {
    error?: string
    processorsByPriority?: Array<ProcessorDetails>
    processorsMap?: Record<ProcessorID, ProcessorDetails>
    products?: Record<PackageID | number, TokenProduct>
    quickbuy?: {
        processorsMap?: Record<ProcessorID, ProcessorDetails>
        processors: Array<ProcessorID | string>
        disable_in_chat: 0 | 1
    }
    smallProducts?: SmallPackages
}

export interface Processor {
    caption: string
    extras?: ProcessorExtrasState
    label: ProcessorName | string
}

export interface ProcessorExtrasState {
    background?: string
    height?: number | string
    width?: number | string
}

export type Processors = Record<ProcessorName | string, Processor>

/**
 * Extra props that are app specific per payment provider
 */
export const processorExtras: Record<string, ProcessorExtrasState> = {
    [ProcessorName.NETBILLING]: {
        background: palette.drawerBackground,
        width: '100%',
        height: 36,
    },
    [ProcessorName.SEGPAY]: {
        background: palette.drawerBackground,
    },
}

export type Packages = Array<{
    id?: PackageID
    caption?: string
    label: string
    state: {
        processor?: Array<ProcessorName | string>
        product?: PackageProduct
        small?: boolean
        wire?: boolean
    }
}>

export const PRODUCT_EXTRAS = {
    [PackageID.TOKENS_200]: '~$0.10 per token',
    [PackageID.TOKENS_550]: '~$0.09 per token',
    [PackageID.TOKENS_900]: '~$0.08 per token',
    [PackageID.TOKENS_1875]: 'less than $0.08 per token',
    [PackageID.TOKENS_3775]: 'less than $0.08 per token',
    [PackageID.TOKENS_7575]: 'less than $0.08 per token',
}

/**
 * Currently only CCBill is available for small token packages ("#4"); this is not specified by the API
 * @param processorsIndex
 * @returns
 */
export const hasSmallPurchaseAccess = (
    processorsIndex: Array<string>,
    level,
): boolean => {
    return (
        processorsIndex.includes(ProcessorID.CCBILL.toString()) &&
        isLevelPremiumOrHigher(level)
    )
}

/**
 * Small Products & metadata are not returned by the API
 */
export const SMALL_PRODUCTS = {
    description: '* only available by credit card through CCBill',
}

/**
 * SmallPackages are not returned by the API as of 2/17/21
 */
export type SmallPackages = Array<{
    volume: number
    label: string
    state: {
        processor: Array<ProcessorName>
        product: PackageProduct
    }
}>

/**
 * Hardcoded Small Packages; as of 2/17/22 only CCBill is available for these
 * Small Products & metadata are not returned by the API
 */
export const smallPackages: SmallPackages = [
    {
        state: {
            product: PackageProduct.TOKENS_10,
            processor: [ProcessorName.CCBILL],
        },
        label: '10 Tokens for $1.49',
        volume: 10,
    },
    {
        state: {
            product: PackageProduct.TOKENS_15,
            processor: [ProcessorName.CCBILL],
        },
        label: '15 Tokens for $1.99',
        volume: 15,
    },
    {
        state: {
            product: PackageProduct.TOKENS_20,
            processor: [ProcessorName.CCBILL],
        },
        label: '20 Tokens for $2.49',
        volume: 20,
    },
    {
        state: {
            product: PackageProduct.TOKENS_25,
            processor: [ProcessorName.CCBILL],
        },
        label: '25 Tokens for $2.99',
        volume: 25,
    },
    {
        state: {
            product: PackageProduct.TOKENS_30,
            processor: [ProcessorName.CCBILL],
        },
        label: '30 Tokens for $3.49',
        volume: 30,
    },
    {
        state: {
            product: PackageProduct.TOKENS_35,
            processor: [ProcessorName.CCBILL],
        },
        label: '35 Tokens for $3.99',
        volume: 35,
    },
    {
        state: {
            product: PackageProduct.TOKENS_40,
            processor: [ProcessorName.CCBILL],
        },
        label: '40 Tokens for $4.49',
        volume: 40,
    },
    {
        state: {
            product: PackageProduct.TOKENS_45,
            processor: [ProcessorName.CCBILL],
        },
        label: '45 Tokens for $4.99',
        volume: 45,
    },
    {
        state: {
            product: PackageProduct.TOKENS_50,
            processor: [ProcessorName.CCBILL],
        },
        label: '50 Tokens for $5.49',
        volume: 50,
    },
    {
        state: {
            product: PackageProduct.TOKENS_55,
            processor: [ProcessorName.CCBILL],
        },
        label: '55 Tokens for $5.99',
        volume: 55,
    },
    {
        state: {
            product: PackageProduct.TOKENS_60,
            processor: [ProcessorName.CCBILL],
        },
        label: '60 Tokens for $6.49',
        volume: 60,
    },
    {
        state: {
            product: PackageProduct.TOKENS_65,
            processor: [ProcessorName.CCBILL],
        },
        label: '65 Tokens for $6.99',
        volume: 65,
    },
    {
        state: {
            product: PackageProduct.TOKENS_70,
            processor: [ProcessorName.CCBILL],
        },
        label: '70 Tokens for $7.49',
        volume: 70,
    },
    {
        state: {
            product: PackageProduct.TOKENS_75,
            processor: [ProcessorName.CCBILL],
        },
        label: '75 Tokens for $7.99',
        volume: 75,
    },
    {
        state: {
            product: PackageProduct.TOKENS_80,
            processor: [ProcessorName.CCBILL],
        },
        label: '80 Tokens for $8.49',
        volume: 80,
    },
    {
        state: {
            product: PackageProduct.TOKENS_85,
            processor: [ProcessorName.CCBILL],
        },
        label: '85 Tokens for $8.99',
        volume: 85,
    },
    {
        state: {
            product: PackageProduct.TOKENS_90,
            processor: [ProcessorName.CCBILL],
        },
        label: '90 Tokens for $9.49',
        volume: 90,
    },
    {
        state: {
            product: PackageProduct.TOKENS_95,
            processor: [ProcessorName.CCBILL],
        },
        label: '95 Tokens for $9.99',
        volume: 95,
    },
]
